import { Component, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions } from 'ag-grid-community';
import { DashboardNavigationService } from 'src/app/navigation/dashboard-navigation.service';
import { SaveDashboardModalData, SystemTemplateWto } from '../../../../shared/models/dashboard.model';
import { CvAngularLoadingOverlayComponent } from '../../../reusables/ag-grid-components/cv-angular-loading-overlay/cv-angular-loading-overlay.component';
import { AgGridHelperService } from '../../../reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from '../../../reusables/services/cv-configuration.service';
import { CVLocalizationService } from '../../../reusables/services/cv-localization.service';
import { DashboardService } from '../../dashboard.service';
import { DescriptionCellRendererComponent } from '../../workspace/manage-available-dashboards/ag-grid-components/description-renderer/description-renderer.component';

@Component({
    selector: 'cv-template-list-modal',
    templateUrl: './template-list-modal.component.html',
    styleUrls: ['./template-list-modal.component.scss']
})
export class TemplateListModalComponent implements OnInit {
    @ViewChild('templateListModalGrid') agGrid: AgGridAngular;

    private gridOptions: GridOptions = null;
    private data: SystemTemplateWto[] = [];
    private isDataLoading = false;
    filterValue = '';
    private localeText = {}; // Object for AG Grid localization
    showLoader = false;

    constructor(
        private ngbActiveModal: NgbActiveModal,
        private cvLocalizationService: CVLocalizationService,
        private agGridHelperService: AgGridHelperService,
        private dashboardService: DashboardService,
        private cvConfigurationService: CvConfigurationService,
        private dashboardNavigationservice: DashboardNavigationService,
    ) {
        this.cvConfigurationService.isCxOne = true;
    }

    async ngOnInit() {
        this.localeText = await this.agGridHelperService.getLocalizations();
        await this.cvLocalizationService.GetSection("Template_List_Modal");
        await this.setupGrid();
        await this.loadData();
    }

    private async loadData() {
        if (this.isDataLoading) {
            return; // To not have multiple outgoing requests
        }
        this.isDataLoading = true;

        try {
            this.data = await this.dashboardService.loadSystemTemplates(true, this.cvConfigurationService.isCxOne);
            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('rowData', this.data);
            }
        }
        finally {
            this.isDataLoading = false;
        }
    }

    private setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            components: {
                cvAngularLoadingComponent: CvAngularLoadingOverlayComponent,
                descriptionCellRenderer: DescriptionCellRendererComponent,
            },
            defaultColDef: {
                sortable: true,
            },
            columnDefs: [
                {
                    field: "SystemTemplateName",
                    colId: "SystemTemplateName",
                    headerName: this.getLocaleText("Name", "Name"),
                    valueFormatter: params => this.sanitizeName(params.value) ?? '',
                    cellRenderer: "descriptionCellRenderer",
                    menuTabs: [],
                    sort: 'asc',
                },
                {
                    field: "SystemTemplateDesc",
                    colId: "SystemTemplateDesc",
                    headerName: this.getLocaleText("Description", "Description"),
                    valueFormatter: params => this.sanitizeName(params.value) ?? '',
                    cellRenderer: 'descriptionCellRenderer',
                    menuTabs: [],
                },
                {
                    field: "UserType",
                    colId: "UserType",
                    headerName: this.getLocaleText("Persona", "Persona"),
                    valueFormatter: params => this.sanitizeName(params.value) ?? '',
                    cellRenderer: 'descriptionCellRenderer',
                    menuTabs: [],
                },
                {
                    field: "SelectedDomains",
                    colId: "SelectedDomains",
                    headerName: this.getLocaleText("Domains", "Domains"),
                    valueFormatter: params => this.sanitizeName(params.value) ?? '',
                    cellRenderer: 'descriptionCellRenderer',
                    menuTabs: [],
                },
            ],
            rowData: [],
            onGridReady: params => {
                params.api.showLoadingOverlay();
            },
            onFirstDataRendered: params => {
                params.api.sizeColumnsToFit();
            },
            onGridSizeChanged: params => {
                params.api.sizeColumnsToFit();
            },
            loadingOverlayComponent: 'cvAngularLoadingComponent',
            getRowId: ({ data: rowData }) => rowData.SystemTemplateID,
            rowSelection: "single",
            suppressMovableColumns: true,
            suppressDragLeaveHidesColumns: true,
            suppressContextMenu: true,
            localeText: this.localeText,
            headerHeight: 45,
            rowHeight: 36,
            icons: {
                sortAscending: '<div class="sortIcons"><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                sortDescending: '<div class="sortIcons"><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
            },
            ensureDomOrder: true,
            rowClass: 'cxOneRow',
        });

    }

    getLocaleText(key: string, defaultValue?: string) {
        return this.cvLocalizationService.getCachedText("Template_List_Modal", key, defaultValue);
    }

    private sanitizeName(name: string) {
        return name?.replace(/</gi, "&#60;").replace(/=/gi, "&#61;").replace(/>/gi, "&#62;");
    }

    isTemplateNotSelected() {
        if (this.agGrid?.api?.getSelectedNodes()[0]) {
            return false;
        } else {
            return true;
        }
    }

    getNumGridRows() {
        if (this.agGrid?.api?.getDisplayedRowCount()) {
            return this.agGrid?.api?.getDisplayedRowCount();
        } else {
            return 0;
        }
    }

    applyFilter() {
        if (this.agGrid?.api) { // notify filter change
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue.trim());
            if (this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0) {
                this.updateNoRowsMessage();
                this.agGrid?.api?.showNoRowsOverlay();
            } else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    };

    private updateNoRowsMessage() {
        // Data load error
        if (this.dashboardService.loadExistingTemplatesFailed) {
            this.gridOptions.overlayNoRowsTemplate =
                `<div>
                    <svg class="cxoneErrorState mb-2">
                        <use href="../App_Themes/CXone/icons/data-error.svg#data-error"></use>
                    </svg>
                    <p class="cxoneEmptyText">${ this.getLocaleText("Load_Error", "Cannot load data") }</p>
                </div>`;
        }
        // No search results
        if (this.filterValue.length > 0) {
            this.gridOptions.overlayNoRowsTemplate =
                `<div>
                    <svg class="cxoneFilterState mb-2" style="padding-top: 32px;">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxoneEmptyText">${ this.getLocaleText("No_Results", "No results found") }</p>
                </div>`;
        }
        // No templates
        if (this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length === 0) {
            this.gridOptions.overlayNoRowsTemplate =
                `<div>
                    <svg class="cxoneEmptyState mb-2">
                        <use href="../App_Themes/CXone/icons/add-dashboard.svg#add-dashboard"></use>
                    </svg>
                    <p class="cxoneEmptyText">${ this.getLocaleText("No_Templates_Added", "No templates added yet") }</p>
                </div>`;
        }
    }

    clearFilter() {
        this.filterValue = "";
        if (this.agGrid?.api) { // Notify filter change
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue);
            this.applyFilter();
        }
    };

    getGridOptions() {
        return this.gridOptions;
    }

    closeModal() {
        this.ngbActiveModal.close();
    }

    selectButtonClicked() {
        if (this.agGrid?.api?.getSelectedNodes()[0] != null) {
            const data: SaveDashboardModalData = {
                dashboardName: this.agGrid?.api?.getSelectedNodes()[0].data.SystemTemplateName,
                description: this.agGrid?.api?.getSelectedNodes()[0].data.SystemTemplateDesc,
                folder: 'CXOne Default',
                key: this.agGrid?.api?.getSelectedNodes()[0].data.SystemTemplateKey,
                iscxone: true,
            };

            this.dashboardService.openSaveDashboardModal(data, 'FromTemplate', false).subscribe(async dashboard => {
                if (dashboard) {
                    this.showLoader = true;
                    await this.dashboardService.clearWorkspace(this.dashboardService.workspaceKey, true);
                    await this.dashboardService.clearDashboardsFromWorkspace();
                    await this.dashboardService.addDashboardToWorkspace(this.dashboardService.workspaceKey, dashboard, true, true);
                    await this.dashboardNavigationservice.navigateToCXOneDashboardView();
                    this.closeModal();
                }
            });
        }
    }
}
